import { noop } from 'utils/noop';
import { useEffect, useState, ReactNode, Children, isValidElement, cloneElement } from 'react';
import styled from 'styled-components';
import { subtleShadow } from 'ui/theme/shadows';
import { colors } from 'ui/theme/colors';
import { quarter } from 'ui/theme/spacing';
import { GroupButtonSizes, GROUP_BUTTON_SIZES } from './constants';

// <fieldset> contains default styling we need override
const StyledButtonGroup = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  min-width: unset;

  ${(props) => (props['aria-disabled'] ? 'cursor: not-allowed;' : '')}
`;

// flex positioning isn't guaranteed to be respected in a fieldset
// https://stackoverflow.com/questions/28078681/why-cant-fieldset-be-flex-containers
const ButtonWrapper = styled.div`
  border-radius: ${quarter};
  display: inline-flex;
  outline: none;
  ${subtleShadow}

  :focus {
    border-color: ${colors.purple100};
  }
`;

export type ButtonGroupProps = {
  /** if multiple items in the button group can be selected */
  allowMultipleSelection?: boolean;
  /** if you can untoggle a selected option */
  allowUnselect?: boolean;
  /** options to use different size buttons */
  buttonSize?: GroupButtonSizes;
  /** the wrapped buttons */
  children: ReactNode;
  /**
   * the id of the child item selected by default
   * @deprecated use `defaultSelectedIds` instead
   */
  defaultSelectedId?: string;
  /** the id of the group buttons selected by default */
  defaultSelectedIds?: string[];
  /** a unique identifier */
  id: string;
  /** if the entire button group is disabled */
  isDisabled?: boolean;
  /** if enabled, keeps a button "pressed" when clicked */
  trackSelected?: boolean;
  /** Change handler */
  onSelect?: (selectedIds: string[]) => void;
};

/**
 * @deprecated - Use ButtonGroup from ui/buttonGroupV2.
 */
export const ButtonGroup = ({
  allowMultipleSelection = false,
  allowUnselect = false,
  buttonSize = GROUP_BUTTON_SIZES.MEDIUM as GroupButtonSizes,
  children,
  defaultSelectedId = '',
  defaultSelectedIds,
  id = '',
  isDisabled = false,
  trackSelected = true,
  onSelect = noop,
  ...props
}: ButtonGroupProps) => {
  if (defaultSelectedId) {
    // throw an error if the default selected item isn't present at config time
    const childIds = Children.map(children, (child) => (child as any)?.props?.id);

    if (childIds && !childIds.includes(defaultSelectedId)) {
      console.error('Cannot default select a missing selection id'); // eslint-disable-line
    }
  }

  const [selectedIds, setSelectedIds] = useState(
    defaultSelectedId ? [defaultSelectedId] : (defaultSelectedIds ?? []),
  );

  /**
   * If the default selected ID changes after the initial load (such as when
   * it's loaded in via an async process like getting pagination back from the
   * server, update the tracked state.
   * TODO: remove `defaultSelectedId` in favor of `defaultSelectedIds` for all use-cases
   */
  useEffect(() => {
    setSelectedIds(defaultSelectedId ? [defaultSelectedId] : (defaultSelectedIds ?? []));
  }, [defaultSelectedId, defaultSelectedIds]);

  /**
   * TODO: `allowUnselect` doesn't work when `allowMultipleSelection` is off
   */
  const handleClick = (elementId: string) => {
    // otherwise handle the selection logic
    const isPreviouslySelected = selectedIds.includes(elementId);
    let newSelectedIds: string[] = [];

    // case 1: toggling an untoggled item with multiple selection adds it
    if (!isPreviouslySelected && allowMultipleSelection) {
      newSelectedIds = [...selectedIds, elementId];
    }
    // case 2: toggling a new item clears previous selections
    else if (!isPreviouslySelected || !allowMultipleSelection) {
      newSelectedIds = [elementId];
    }
    // case 3: untoggling a previously toggled item removes it
    else if (isPreviouslySelected && allowUnselect) {
      newSelectedIds = selectedIds.filter((sId) => sId !== elementId);
    }

    setSelectedIds(newSelectedIds);
    onSelect(newSelectedIds);
  };

  return (
    <StyledButtonGroup
      role="group"
      aria-disabled={isDisabled}
      data-test={`button-group-${id}`}
      {...props}
    >
      <ButtonWrapper tabIndex={isDisabled ? 0 : undefined}>
        {Children.map(children, (child, idx) => {
          if (isValidElement(child)) {
            const updatedProps = {
              buttonSize,
              handleClick: trackSelected ? handleClick : noop, // only add the click handler for state tracking
              isGroupDisabled: isDisabled,
              isSelected: selectedIds.includes(child.props.id),
              key: child.props.id || `btn-grp-child-${idx}`,
            };

            return cloneElement(child, updatedProps);
          }

          return null;
        })}
      </ButtonWrapper>
    </StyledButtonGroup>
  );
};

export default ButtonGroup;
